import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Database,
  HardDrive,
  Zap,
  Layers,
  Server,
  X,
} from 'lucide-react';
import { DBExportModal } from './DBExportModal';

export interface DatabaseDetailsPanelProps {
  db: any;
  detectedEngine: string;
  onClose: () => void;
  clusterId?: string | number;
  clientId?: string | number;
}

const formatSize = (val: any): string => {
  if (val === null || val === undefined || val === '' || val === '-' || val === '—') return '—';
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (/[a-zA-Z]/.test(trimmed)) return trimmed;
    const num = parseFloat(trimmed);
    if (!isNaN(num)) val = num;
    else return trimmed;
  }
  if (typeof val === 'number') {
    if (val === 0) return '0.00 MB';
    if (val >= 100000) {
      if (val >= 1024 * 1024 * 1024) return `${(val / (1024 * 1024 * 1024)).toFixed(2)} GB`;
      if (val >= 1024 * 1024) return `${(val / (1024 * 1024)).toFixed(2)} MB`;
      if (val >= 1024) return `${(val / (1024 * 1024)).toFixed(2)} KB`;
      return `${val.toFixed(2)} B`;
    }
    if (val >= 1024) return `${(val / 1024).toFixed(2)} GB`;
    if (val >= 0.001) return `${val.toFixed(2)} MB`;
    return '0.00 MB';
  }
  return String(val);
};

export const DatabaseDetailsPanel: React.FC<DatabaseDetailsPanelProps> = ({
  db,
  detectedEngine,
  onClose,
  clusterId,
  clientId,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isSystem = db?.category === 'System';

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex justify-end">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Slide-over Drawer */}
      <div className="relative w-full max-w-lg sm:max-w-xl bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col h-full z-10 animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/75 dark:bg-gray-900/90 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2.5 bg-orange-500/10 border border-orange-500/20 rounded-xl text-orange-500 shrink-0">
              <Database className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate font-mono">
                  {db.database}
                </h3>
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${isSystem
                      ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20'
                      : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                    }`}
                >
                  {db.category}
                </span>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
                  {db.db_type || detectedEngine}
                </span>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {db.db_name && db.collection ? `Collection: ${db.collection} • Database: ${db.db_name}` : 'Database Inventory Details'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 ml-2">
            <DBExportModal
              endpoint="/db_monitor_dashboard/schema/stats"
              isdatabase={true}
              databasename={db.database || db.db_name || ''}
              cluster_id={clusterId}
              client_id={clientId}
              buttonText="Export"
              title={`Export ${db.database || db.db_name || 'Database'}`}
              subtitle="Export database overview details to Excel or CSV"
            />
            <button
              onClick={onClose}
              className="p-1.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors shrink-0"
              title="Close Panel (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Body - Directly shows metrics & overview data */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-5">
          {/* Storage & Capacity */}
          <div>
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2.5 flex items-center gap-1.5">
              <HardDrive className="w-3.5 h-3.5 text-orange-500" />
              Storage & Capacity
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                  Total Size
                </span>
                <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                  {formatSize(db.total_size)}
                </span>
              </div>

              {(db.data_size_mb != null || db.storage_size_mb != null) && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Data Size
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {formatSize(db.data_size_mb ?? db.storage_size_mb ?? 0)}
                  </span>
                </div>
              )}

              {db.index_size_mb != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Index Size
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {formatSize(db.index_size_mb)}
                  </span>
                </div>
              )}

              {db.compressed_size != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Compressed
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {formatSize(db.compressed_size)}
                  </span>
                </div>
              )}

              {db.uncompressed_data_size != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Uncompressed
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {formatSize(db.uncompressed_data_size)}
                  </span>
                </div>
              )}

              {db.data_file_used_mb != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Data File Used
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {formatSize(db.data_file_used_mb)}
                  </span>
                </div>
              )}

              {db.log_file_used_mb != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Log File Used
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {formatSize(db.log_file_used_mb)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Objects & Schema Metrics */}
          <div>
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2.5 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-orange-500" />
              Objects & Records
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {db.table_count != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Tables
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {Number(db.table_count).toLocaleString()}
                  </span>
                </div>
              )}

              {db.view_count != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Views
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {Number(db.view_count).toLocaleString()}
                    {db.system_view_count ? ` (${db.system_view_count} sys)` : ''}
                  </span>
                </div>
              )}

              {db.docs_count != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Documents
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {Number(db.docs_count).toLocaleString()}
                  </span>
                </div>
              )}

              {db.row_estimate != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Row Estimate
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {Number(db.row_estimate).toLocaleString()}
                  </span>
                </div>
              )}

              {db.index_count != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Index Count
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {db.index_count}
                  </span>
                </div>
              )}

              {db.avg_obj_size_b != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Avg Doc Size
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {formatSize(db.avg_obj_size_b / (1024 * 1024))}
                  </span>
                </div>
              )}

              {db.active_parts != null && (
                <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                    Active Parts
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                    {db.active_parts}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Activity & Performance Metrics */}
          {(db.connections != null ||
            db.active_connections != null ||
            db.open_txns != null ||
            db.cache_hit_pct != null ||
            db.commits_per_s != null ||
            db.transactions_per_s != null ||
            db.active_merges != null ||
            db.pending_mutations != null) && (
              <div>
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2.5 flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-orange-500" />
                  Activity & Performance
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {(db.connections != null || db.active_connections != null) && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                        Connections
                      </span>
                      <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                        {db.active_connections ?? db.connections ?? 0}
                      </span>
                    </div>
                  )}

                  {db.open_txns != null && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                        Open Transactions
                      </span>
                      <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                        {db.open_txns}
                      </span>
                    </div>
                  )}

                  {db.cache_hit_pct != null && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                        Cache Hit Ratio
                      </span>
                      <span className="text-base font-bold text-emerald-600 dark:text-emerald-400 font-mono mt-1 block">
                        {Number(db.cache_hit_pct).toFixed(1)}%
                      </span>
                    </div>
                  )}

                  {db.transactions_per_s != null && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                        Transactions / sec
                      </span>
                      <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                        {Number(db.transactions_per_s).toFixed(2)}/s
                      </span>
                    </div>
                  )}

                  {db.commits_per_s != null && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                        Commits / sec
                      </span>
                      <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                        {Number(db.commits_per_s).toFixed(2)}/s
                      </span>
                    </div>
                  )}

                  {db.active_merges != null && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                        Active Merges
                      </span>
                      <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                        {db.active_merges}
                      </span>
                    </div>
                  )}

                  {db.pending_mutations != null && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block truncate">
                        Pending Mutations
                      </span>
                      <span className="text-base font-bold text-gray-900 dark:text-white font-mono mt-1 block">
                        {db.pending_mutations}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* System & Metadata Information Table */}
          <div>
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2.5 flex items-center gap-1.5">
              <Server className="w-3.5 h-3.5 text-orange-500" />
              Metadata & Configuration
            </h4>
            <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl overflow-hidden divide-y divide-gray-200 dark:divide-gray-700/40 text-xs">
              <div className="p-3 flex items-center justify-between gap-3">
                <span className="text-gray-500 dark:text-gray-400 font-medium">Database Name</span>
                <span className="font-mono font-bold text-gray-900 dark:text-white text-right truncate">
                  {db.database}
                </span>
              </div>
              <div className="p-3 flex items-center justify-between gap-3">
                <span className="text-gray-500 dark:text-gray-400 font-medium">Engine / Dialect</span>
                <span className="font-mono font-bold text-orange-600 dark:text-orange-400 uppercase">
                  {db.db_type || detectedEngine}
                </span>
              </div>
              <div className="p-3 flex items-center justify-between gap-3">
                <span className="text-gray-500 dark:text-gray-400 font-medium">Category</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{db.category}</span>
              </div>
              {db.size_source && (
                <div className="p-3 flex items-center justify-between gap-3">
                  <span className="text-gray-500 dark:text-gray-400 font-medium">Size Source</span>
                  <span className="font-mono text-gray-700 dark:text-gray-300">{db.size_source}</span>
                </div>
              )}
              {db.last_seen && (
                <div className="p-3 flex items-center justify-between gap-3">
                  <span className="text-gray-500 dark:text-gray-400 font-medium">Last Seen / Polled</span>
                  <span className="font-mono text-gray-700 dark:text-gray-300">
                    {new Date(db.last_seen).toLocaleString()}
                  </span>
                </div>
              )}
              {db.recovery_model && (
                <div className="p-3 flex items-center justify-between gap-3">
                  <span className="text-gray-500 dark:text-gray-400 font-medium">Recovery Model</span>
                  <span className="font-mono text-gray-700 dark:text-gray-300">{db.recovery_model}</span>
                </div>
              )}
              {db.status && (
                <div className="p-3 flex items-center justify-between gap-3">
                  <span className="text-gray-500 dark:text-gray-400 font-medium">Engine Status</span>
                  <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400 uppercase">
                    {db.status}
                  </span>
                </div>
              )}
              {db.backup_flags && (
                <div className="p-3 flex items-center justify-between gap-3">
                  <span className="text-gray-500 dark:text-gray-400 font-medium">Backup Flags</span>
                  <span className="font-mono text-amber-600 dark:text-amber-400">
                    {Array.isArray(db.backup_flags) ? db.backup_flags.join(', ') : String(db.backup_flags)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
